import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FileUploadInput } from "@/components/ui/file-upload-input";
import { DocumentType, DOCUMENT_TYPE_LABELS, DocumentStatus } from "@/types/documento";
import { useUploadDocumento } from "@/services/queries/useDocumentos";

const STAGE_LABELS: Record<DocumentStatus, string> = {
  "Produção": "Produção",
  "Pré-Produção": "Pré-Produção",
  "Pós-Produção": "Pós-Produção",
  "Desenvolvimento": "Desenvolvimento",
  "Comercialização": "Comercialização",
};

const uploadSchema = z.object({
  fileName: z.string().min(3, "Nome do arquivo deve ter pelo menos 3 caracteres"),
  documentType: z.enum(["contratos", "projetos", "notas-fiscais", "planilhas"] as const, {
    required_error: "Tipo de documento é obrigatório",
  }),
  stage: z.enum(["Produção", "Pré-Produção", "Pós-Produção", "Desenvolvimento", "Comercialização"] as const, {
    required_error: "Etapa é obrigatória",
  }),
  file: z.instanceof(File, { message: "Arquivo é obrigatório" }),
  description: z.string().optional(),
});

type UploadFormData = z.infer<typeof uploadSchema>;

interface UploadDocumentDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * UploadDocumentDialog Component
 *
 * Dialog for uploading new documents with form validation.
 * Uses React Hook Form + Zod for validation and useUploadDocumento hook for mutation.
 *
 * @param open - Whether the dialog is open
 * @param onClose - Callback to close the dialog
 * @param onSuccess - Callback when upload is successful
 *
 * @example
 * ```tsx
 * <UploadDocumentDialog
 *   open={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   onSuccess={() => {
 *     setIsOpen(false);
 *     // Optionally refresh data
 *   }}
 * />
 * ```
 */
export function UploadDocumentDialog({
  open,
  onClose,
  onSuccess,
}: UploadDocumentDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const uploadMutation = useUploadDocumento();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
  } = useForm<UploadFormData>({
    resolver: zodResolver(uploadSchema),
  });

  const documentType = watch("documentType");
  const stage = watch("stage");

  const handleClose = () => {
    reset();
    setFile(null);
    onClose();
  };

  const onSubmit = async (data: UploadFormData) => {
    const formData = new FormData();
    formData.append("file", data.file);
    formData.append("fileName", data.fileName);
    formData.append("documentType", data.documentType);
    formData.append("stage", data.stage);
    if (data.description) {
      formData.append("description", data.description);
    }

    uploadMutation.mutate(formData, {
      onSuccess: () => {
        handleClose();
        onSuccess();
      },
    });
  };

  const handleFileChange = (selectedFile: File | null) => {
    setFile(selectedFile);
    if (selectedFile) {
      setValue("file", selectedFile, { shouldValidate: true });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-[#15203C] border-[#5D5D5D] max-w-[600px] p-[30px]">
        <DialogHeader className="mb-[20px]">
          <DialogTitle className="text-[24px] font-bold text-white font-['Plus_Jakarta_Sans']">
            Enviar Documento
          </DialogTitle>
          <p className="text-[14px] text-[#D1D1D1] font-['Plus_Jakarta_Sans'] mt-[8px]">
            Preencha as informações abaixo para fazer upload do documento
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-[20px]">
          {/* File Name */}
          <div className="space-y-[8px]">
            <Label htmlFor="fileName" className="text-white text-[14px] font-semibold">
              Nome do Arquivo
            </Label>
            <Input
              id="fileName"
              {...register("fileName")}
              placeholder="Digite o nome do arquivo"
              className="bg-[#0C1427] border-[#5D5D5D] text-[#F6F6F6]"
            />
            {errors.fileName && (
              <p className="text-[12px] text-[#DC4426] font-['Plus_Jakarta_Sans']">
                {errors.fileName.message}
              </p>
            )}
          </div>

          {/* Document Type */}
          <div className="space-y-[8px]">
            <Label htmlFor="documentType" className="text-white text-[14px] font-semibold">
              Tipo de Documento
            </Label>
            <Select
              value={documentType}
              onValueChange={(value) => setValue("documentType", value as DocumentType, { shouldValidate: true })}
            >
              <SelectTrigger className="bg-[#0C1427] border-[#5D5D5D] text-[#F6F6F6] h-[50px]">
                <SelectValue placeholder="Selecione o tipo" />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(DOCUMENT_TYPE_LABELS) as DocumentType[]).map((type) => (
                  <SelectItem key={type} value={type}>
                    {DOCUMENT_TYPE_LABELS[type]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.documentType && (
              <p className="text-[12px] text-[#DC4426] font-['Plus_Jakarta_Sans']">
                {errors.documentType.message}
              </p>
            )}
          </div>

          {/* Stage */}
          <div className="space-y-[8px]">
            <Label htmlFor="stage" className="text-white text-[14px] font-semibold">
              Etapa
            </Label>
            <Select
              value={stage}
              onValueChange={(value) => setValue("stage", value as DocumentStatus, { shouldValidate: true })}
            >
              <SelectTrigger className="bg-[#0C1427] border-[#5D5D5D] text-[#F6F6F6] h-[50px]">
                <SelectValue placeholder="Selecione a etapa" />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(STAGE_LABELS) as DocumentStatus[]).map((stageOption) => (
                  <SelectItem key={stageOption} value={stageOption}>
                    {STAGE_LABELS[stageOption]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.stage && (
              <p className="text-[12px] text-[#DC4426] font-['Plus_Jakarta_Sans']">
                {errors.stage.message}
              </p>
            )}
          </div>

          {/* File Upload */}
          <div className="space-y-[8px]">
            <FileUploadInput
              label="Arquivo"
              value={file}
              onChange={handleFileChange}
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
            />
            {errors.file && (
              <p className="text-[12px] text-[#DC4426] font-['Plus_Jakarta_Sans']">
                {errors.file.message}
              </p>
            )}
          </div>

          {/* Description (Optional) */}
          <div className="space-y-[8px]">
            <Label htmlFor="description" className="text-white text-[14px] font-semibold">
              Descrição (Opcional)
            </Label>
            <Textarea
              id="description"
              {...register("description")}
              placeholder="Digite uma descrição para o documento"
              className="bg-[#0C1427] border-[#5D5D5D] text-[#F6F6F6] min-h-[100px]"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-[12px] mt-[24px]">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="bg-transparent border-[#5D5D5D] text-[#F6F6F6] hover:bg-white/5"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="default"
              isLoading={uploadMutation.isPending}
              className="bg-[#7846FF] hover:bg-[#7846FF]/80 text-white"
            >
              Enviar Documento
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
